/**
 * Client Retention stat cards (WS3), three rows, mounted in BOTH
 * RefinanceIntelligence page variants:
 *   Row 1: Total Discharges / Confirmed Refinances / Returning Clients / Needs Review
 *   Row 2: Refinance Rate / Acquired to Primary / Primary Book Refinances / Win-Back Rate
 *   Row 3: Refinance Commission + Loan Value / Returning Client Commission + Loan Value
 *
 * Returning clients are counted and valued separately from refinances and are
 * never folded into the refinance rate or recovered-money totals (WS1 gating).
 */
import React from 'react';
import type { RefinanceMetrics } from '../../hooks/useRefinanceData';
import { formatCurrency } from '../../utils/formatters';

interface RefinanceStatCardsProps {
  metrics: RefinanceMetrics | null;
  acquiredOnlyFilter: boolean;
  onToggleAcquiredOnly: () => void;
  /** Optional: focus the Needs Review tab when the card is clicked. */
  onNeedsReviewClick?: () => void;
}

const CARD = 'bg-gray-800 rounded-lg p-5 border border-gray-700';

const RefinanceStatCards: React.FC<RefinanceStatCardsProps> = ({
  metrics,
  acquiredOnlyFilter,
  onToggleAcquiredOnly,
  onNeedsReviewClick,
}) => {
  if (!metrics) return null;

  const returningClients = metrics.returningClients ?? 0;
  // A confirmed match is either a refinance or a returning client, so
  // confirmed refinances = confirmed minus confirmed returning clients.
  const confirmedRefinances = Math.max(0, metrics.confirmed - returningClients);
  const returningCommissionCents = metrics.returningClientCommissionCents ?? 0;
  const returningLoanAmountCents = metrics.returningClientLoanAmountCents ?? 0;

  return (
    <div className="space-y-4">
      {/* Row 1: Outcome overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Total Discharges</p>
          <p className="text-3xl font-bold text-gray-300">{metrics.totalDischarges}</p>
          <p className="text-xs text-gray-500 mt-1">All time</p>
        </div>
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Confirmed Refinances</p>
          <p className="text-3xl font-bold text-cyan-400">{confirmedRefinances}</p>
          <p className="text-xs text-gray-500 mt-1">Reviewer-confirmed refinances</p>
        </div>
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Returning Clients</p>
          <p className="text-3xl font-bold text-purple-400">{returningClients}</p>
          <p className="text-xs text-gray-500 mt-1">Came back after leaving</p>
        </div>
        {onNeedsReviewClick ? (
          <button onClick={onNeedsReviewClick} className={`${CARD} text-left transition-all hover:bg-gray-750`}>
            <p className="text-sm text-gray-400 mb-1">Needs Review</p>
            <p className="text-3xl font-bold text-yellow-400">{metrics.pendingVerification}</p>
            <p className="text-xs text-gray-500 mt-1">Click to review</p>
          </button>
        ) : (
          <div className={CARD}>
            <p className="text-sm text-gray-400 mb-1">Needs Review</p>
            <p className="text-3xl font-bold text-yellow-400">{metrics.pendingVerification}</p>
            <p className="text-xs text-gray-500 mt-1">Awaiting a category</p>
          </div>
        )}
      </div>

      {/* Row 2: Rates and book movement */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Refinance Rate</p>
          <p className="text-3xl font-bold text-green-400">{metrics.retentionRate}%</p>
          <p className="text-xs text-gray-500 mt-1">Of discharges refinanced back</p>
        </div>
        <button
          onClick={onToggleAcquiredOnly}
          className={`${CARD} text-left transition-all hover:bg-gray-750 ${
            acquiredOnlyFilter ? 'border-cyan-500 ring-1 ring-cyan-500/50' : ''
          }`}
        >
          <p className="text-sm text-gray-400 mb-1">Acquired to Primary</p>
          <p className="text-3xl font-bold text-cyan-400">{metrics.acquiredToPrimary}</p>
          <p className={`text-xs mt-1 ${acquiredOnlyFilter ? 'text-cyan-400' : 'text-gray-500'}`}>
            {acquiredOnlyFilter ? 'Click to show all' : 'Click to filter'}
          </p>
        </button>
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Primary Book Refinances</p>
          <p className="text-3xl font-bold text-purple-400">{metrics.primaryBookRefinances}</p>
          <p className="text-xs text-gray-500 mt-1">Same loan book refinances</p>
        </div>
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Win-Back Rate</p>
          <p className="text-3xl font-bold text-green-400">{metrics.winBackRate}%</p>
          <p className="text-xs text-gray-500 mt-1">Of confirmed matches</p>
        </div>
      </div>

      {/* Row 3: Money retained, split refinance vs returning client */}
      <div className="grid grid-cols-2 gap-4">
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Refinance Commission + Loan Value</p>
          <p className="text-2xl font-bold text-green-400">{formatCurrency(metrics.totalRecoveredCommissionCents)}</p>
          <p className="text-xs text-gray-500 mt-1">Loan value {formatCurrency(metrics.totalRecoveredLoanAmountCents)}</p>
        </div>
        <div className={CARD}>
          <p className="text-sm text-gray-400 mb-1">Returning Client Commission + Loan Value</p>
          <p className="text-2xl font-bold text-purple-400">{formatCurrency(returningCommissionCents)}</p>
          <p className="text-xs text-gray-500 mt-1">Loan value {formatCurrency(returningLoanAmountCents)}</p>
        </div>
      </div>
    </div>
  );
};

export default RefinanceStatCards;
